/**
 * Single-row, horizontally scrolling list of links.
 *
 * Scrolling is native (touch, trackpad, Shift + wheel, keyboard focus). This element adds:
 * - Prev / next buttons, shown only while there is content to scroll to
 * - Edge fade state classes (has-more-start / has-more-end)
 * - Click-and-drag scrolling for mouse users. The buttons are the non-dragging alternative.
 */
class SubCategoriesScroller extends HTMLElement {
  constructor() {
    'use strict';
    super();

    this.dragThreshold = 5;
    this.drag = null;
    this.suppressClick = false;
    this.frame = null;
  }

  connectedCallback() {
    this.track = this.querySelector('[data-scroll-track]');
    this.prevButton = this.querySelector('[data-scroll-prev]');
    this.nextButton = this.querySelector('[data-scroll-next]');
    if (!this.track) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.onScroll = () => this.requestUpdate();
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.resizeObserver = new ResizeObserver(() => this.requestUpdate());
    this.resizeObserver.observe(this.track);

    this.prevButton.addEventListener('click', () => this.scrollByPage(-1));
    this.nextButton.addEventListener('click', () => this.scrollByPage(1));

    this.track.addEventListener('pointerdown', this.onPointerDown.bind(this));
    this.track.addEventListener('pointermove', this.onPointerMove.bind(this));
    this.track.addEventListener('pointerup', this.onPointerEnd.bind(this));
    this.track.addEventListener('pointercancel', this.onPointerEnd.bind(this));
    // A drag must not follow the link it ended on
    this.track.addEventListener('click', this.onClick.bind(this), true);
    this.track.addEventListener('dragstart', (event) => event.preventDefault());

    this.update();
  }

  disconnectedCallback() {
    if (this.track) this.track.removeEventListener('scroll', this.onScroll);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  requestUpdate() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  }

  update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const scrollable = scrollWidth - clientWidth > 1;
    const hasMoreStart = scrollable && scrollLeft > 1;
    const hasMoreEnd = scrollable && scrollLeft + clientWidth < scrollWidth - 1;

    // Classic scrollbars (e.g. macOS with a mouse) take layout space inside the list,
    // overlay ones (touch devices) do not. Let the CSS centre the arrows on the chips only.
    this.style.setProperty('--scrollbar-size', `${this.track.offsetHeight - this.track.clientHeight}px`);

    this.classList.toggle('has-more-start', hasMoreStart);
    this.classList.toggle('has-more-end', hasMoreEnd);
    this.track.classList.toggle('is-scrollable', scrollable);
    const focused = document.activeElement;
    this.prevButton.hidden = !hasMoreStart;
    this.nextButton.hidden = !hasMoreEnd;

    // A hidden button loses focus. Hand it to the other arrow so keyboard users keep their place.
    if (focused === this.nextButton && !hasMoreEnd && hasMoreStart) this.prevButton.focus();
    if (focused === this.prevButton && !hasMoreStart && hasMoreEnd) this.nextButton.focus();
  }

  scrollByPage(direction) {
    this.track.scrollBy({
      left: direction * this.track.clientWidth * 0.8,
      behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
    });
  }

  onPointerDown(event) {
    // Only mouse drags; touch and pen scroll natively
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (this.track.scrollWidth <= this.track.clientWidth) return;

    this.drag = { startX: event.clientX, startScroll: this.track.scrollLeft, active: false };
  }

  onPointerMove(event) {
    if (!this.drag) return;

    // The button was released outside the row (so no pointerup reached us): end the drag
    if ((event.buttons & 1) === 0) {
      this.onPointerEnd(event);
      return;
    }

    const delta = event.clientX - this.drag.startX;
    if (!this.drag.active) {
      if (Math.abs(delta) < this.dragThreshold) return;
      this.drag.active = true;
      this.track.classList.add('is-dragging');
      this.track.setPointerCapture(event.pointerId);
    }

    this.track.scrollLeft = this.drag.startScroll - delta;
  }

  onPointerEnd(event) {
    if (!this.drag) return;

    if (this.drag.active) {
      this.suppressClick = true;
      this.track.classList.remove('is-dragging');
      if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
      // The click that follows a drag fires right after pointerup; clear the flag afterwards
      setTimeout(() => { this.suppressClick = false; }, 0);
    }
    this.drag = null;
  }

  onClick(event) {
    if (!this.suppressClick) return;
    event.preventDefault();
    event.stopPropagation();
  }
}

if (customElements.get('sub-categories-scroller') === undefined) {
  customElements.define('sub-categories-scroller', SubCategoriesScroller);
}
